import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import Navbar from '../components/Navbar';
import { nutrientQueueService, nutrientService } from '../utils/api'; // Use new nutrient services
import LoadingSpinner from '../components/LoadingSpinner';
import BulkNutrientSection from '../components/BulkNutrientSection'; 
import DeleteDishModal from '../components/DeleteDishModal'; // Keep for deleting dishes from queue

const NutrientQueuePage = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedItem, setSelectedItem] = useState(null);
  const [nutrientValues, setNutrientValues] = useState({}); // State for input values
  const [initError, setInitError] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const currentSchoolId = useSelector((state) => state.language?.currentSchoolId || null);
  const [activeTab, setActiveTab] = useState('individual'); // 'individual' or 'bulk'
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  const selectedLanguages = useSelector(state => state.language.selectedLanguages || []);
  const defaultLanguageCode = useSelector(state => state.language.defaultLanguageCode || 'en');

  const getSchoolId = useCallback(() => {
    if (currentSchoolId) return currentSchoolId;
    if (userDetails?.current_school?.id) return userDetails.current_school.id;
    return null;
  }, [currentSchoolId, userDetails]);
  
  const schoolId = getSchoolId();
  const uiLanguage = i18n.language || 'en';

  // Fetch enabled nutrients for the school (needed for labels and units)
  const { 
    data: enabledNutrients = [], 
    isLoading: isLoadingNutrients, 
    isError: isNutrientsError 
  } = useQuery({
    queryKey: ['enabledNutrients', schoolId, uiLanguage], // Use uiLanguage here
    queryFn: () => nutrientService.listAllNutrients(schoolId, uiLanguage, defaultLanguageCode),
    enabled: !!schoolId,
    select: (data) => data.filter(n => n.isEnabled), // Filter for enabled only
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch the nutrient queue (dishes missing values)
  const { 
    data: items = [],
    isLoading: loadingQueue,
    isError: isQueueError, 
    error: queueFetchError,
    refetch: refetchQueue
  } = useQuery({
    queryKey: ['nutrientQueue', schoolId], 
    queryFn: async () => {
      console.log(`[NutrientQueue] Fetching queue for school ${schoolId}`);
      const data = await nutrientQueueService.getMissingNutrients(schoolId);
      console.log(`[NutrientQueue] Received ${data?.length ?? 0} items.`);
      return data || [];
    },
    enabled: !!schoolId, // Only run if schoolId is available
    staleTime: 1 * 60 * 1000, // Cache for 1 minute
    refetchOnMount: true, // Always refetch when component mounts
  });
  
  // Loading state combines both queries
  const loading = loadingQueue || (!!schoolId && isLoadingNutrients);

  const stats = {
    total: items.length,
    completed: 0 // Simplification: Not tracking completed items separately
  };
  
  // Effect to load user data (same as TranslationQueue)
  useEffect(() => {
    try {
      setInitError(null);
      const userData = localStorage.getItem('user');
      if (userData) {
        setUserDetails(JSON.parse(userData));
      } else {
        setInitError('You must be logged in to view this page');
      }
    } catch (err) {
      setInitError('Error loading user data: ' + err.message);
    }
  }, []);

  // Effect to refresh the nutrient queue when the component mounts
  useEffect(() => {
    if (schoolId) {
      // Check if menu types were updated in settings page
      const menuTypeUpdated = sessionStorage.getItem('menuTypeUpdated');
      
      if (menuTypeUpdated === 'true') {
        console.log('[NutrientQueue] Menu types were updated, forcing refresh of nutrient queue');
        queryClient.invalidateQueries({ queryKey: ['nutrientQueue', schoolId] });
        // Clear the flag to prevent unnecessary refreshes
        sessionStorage.removeItem('menuTypeUpdated');
        sessionStorage.removeItem('menuTypeUpdatedTimestamp');
      } else {
        console.log(`[NutrientQueue] Invalidating nutrient queue on component mount for school ${schoolId}`);
        queryClient.invalidateQueries({ queryKey: ['nutrientQueue', schoolId] });
      }
    }
  }, [schoolId, queryClient]);

  // Effect to select the first item when the queue loads/changes
  useEffect(() => {
    if (!loading && !isQueueError && items.length > 0 && !selectedItem) {
      setSelectedItem(items[0]);
    } else if (!loading && items.length === 0 && selectedItem) {
      setSelectedItem(null);
      setNutrientValues({});
    }
  }, [items, loading, isQueueError, selectedItem]);

  // Effect to initialize nutrient values when item is selected (clears old values)
  useEffect(() => {
    if (selectedItem) {
      // Initialize with empty strings for missing nutrients
      const initialValues = {};
      selectedItem.missingNutrients?.forEach(nutrientId => {
        initialValues[nutrientId] = ''; 
      });
      // TODO: Optionally pre-fill with existing values if API provided them
      setNutrientValues(initialValues);
    } else {
      setNutrientValues({}); // Clear values if no item is selected
    }
  }, [selectedItem]);
  
  // --- Mutations --- 
  // Moved this declaration up
  const saveMutation = useMutation({
    // Fix: Unpack the object parameters to match the API function's expected signature
    mutationFn: (params) => nutrientQueueService.saveNutrientValues(
      params.dishId, 
      params.nutrientValues, 
      params.schoolId
    ),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['nutrientQueue', schoolId] });
      const previousItems = queryClient.getQueryData(['nutrientQueue', schoolId]);
      // Optimistic update: remove the saved item from the list
      const updatedItems = previousItems?.filter(item => item.id !== variables.dishId) || [];
      queryClient.setQueryData(['nutrientQueue', schoolId], updatedItems);
      console.log(`[NutrientQueueSave] Optimistically removed item ${variables.dishId}`);
      return { previousItems };
    },
    onError: (err, variables, context) => {
      console.error(`[NutrientQueueSave] Error saving nutrients for dish ${variables.dishId}:`, err);
      if (context?.previousItems) {
        queryClient.setQueryData(['nutrientQueue', schoolId], context.previousItems);
      }
      // TODO: Display user-friendly error message
    },
    onSettled: (data, error, variables, context) => {
      // Invalidate or refetch to ensure consistency, even after optimistic update
      queryClient.invalidateQueries({ queryKey: ['nutrientQueue', schoolId] });
      console.log(`[NutrientQueueSave] Invalidated queue query after saving dish ${variables.dishId}`);
      
      // --- ADD INVALIDATION FOR PUBLIC MENU HERE ---
      const subdomain = userDetails?.current_school?.subdomain; 
      if (subdomain) {
        console.log(`[NutrientQueueSave] Invalidating public menu cache for subdomain: ${subdomain}`);
        // Invalidate the public menu query to refresh nutrient status
        queryClient.invalidateQueries({ queryKey: ['publicMenu', subdomain], refetchType: 'all' }); 
      } else {
        console.warn('[NutrientQueueSave] Could not invalidate public menu cache: subdomain not found in userDetails.');
      }
      // --- END PUBLIC MENU INVALIDATION ---

      // Select the next item if successful and items remain
      if (!error) {
        const currentItems = queryClient.getQueryData(['nutrientQueue', schoolId]) || context?.previousItems || [];
        const savedItemIndex = currentItems.findIndex(item => item.id === variables.dishId); 
        const nextIndex = (savedItemIndex !== -1 && savedItemIndex < currentItems.length -1) ? savedItemIndex : 0;
        const nextItem = currentItems.length > 1 ? currentItems[nextIndex] : null;
        setSelectedItem(nextItem); // Directly set state instead of calling handleSelectItem
      }
    }
  });

  // --- Helper Functions (Adapted) ---

  const handleSelectItem = useCallback((item) => {
    setSelectedItem(item);
    saveMutation.reset(); // Reset mutation state
    // Initialization happens in the useEffect above
  }, [saveMutation]); // Now saveMutation is already defined when this is called
  
  const handleNutrientValueChange = useCallback((nutrientId, value) => {
    setNutrientValues(prev => ({
      ...prev,
      [nutrientId]: value // Store as string, validation on save
    }));
  }, []);
  
  // Memoize map for performance
  const nutrientMap = useMemo(() => {
    return enabledNutrients.reduce((acc, nutrient) => {
      acc[nutrient.id] = nutrient; // Store the whole nutrient object
      return acc;
    }, {});
  }, [enabledNutrients]);

  const getNutrientInfo = useCallback((id) => {
    return nutrientMap[id] || { id, name: `Nutrient ${id}`, unit: null, isEnabled: false };
  }, [nutrientMap]);

  const isAtLeastOneValueFilled = useCallback(() => {
    if (!selectedItem) return false;
    // Check if at least one value in the current `nutrientValues` state is non-empty
    return Object.values(nutrientValues).some(val => val !== null && val.trim() !== '');
  }, [selectedItem, nutrientValues]);
  
  const getDisplayType = useCallback((type = 'dish') => { // Default to dish
    return t('admin.dish'); // Only handling dishes in this queue
  }, [t]);
  
  // Use fallback logic similar to TranslationQueue
  const getDisplayName = useCallback((item) => {
    if (!item) return '';
    const allTrans = item.allTranslations || {};
    const uiLang = i18n.language;
    const schoolDefaultLang = defaultLanguageCode;
    const schoolSelectedLangsSet = new Set((selectedLanguages || []).map(l => l.code));
    const isSelectedLang = (code) => schoolSelectedLangsSet.has(code);

    if (isSelectedLang(uiLang) && allTrans[uiLang]) return allTrans[uiLang];
    if (isSelectedLang(schoolDefaultLang) && allTrans[schoolDefaultLang]) return allTrans[schoolDefaultLang];
    const otherSelectedLangs = Array.from(schoolSelectedLangsSet)
                                   .filter(code => code !== uiLang && code !== schoolDefaultLang)
                                   .sort();
    for (const code of otherSelectedLangs) {
      if (allTrans[code]) return allTrans[code];
    }
    const firstAvailable = Object.entries(allTrans).sort(([langA], [langB]) => langA.localeCompare(langB)).find(([_, name]) => !!name);
    if (firstAvailable) return firstAvailable[1];
    return `${getDisplayType(item.type)} #${item.id}`;
  }, [i18n.language, defaultLanguageCode, selectedLanguages, getDisplayType]);
  
  const handleSaveChanges = useCallback(async () => {
    if (!selectedItem || !isAtLeastOneValueFilled() || saveMutation.isLoading) return;

    // Filter out empty/invalid values before sending
    const valuesToSave = {};
    let hasValidValue = false;
    Object.entries(nutrientValues).forEach(([id, value]) => {
      if (value !== null && value.trim() !== '') {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue >= 0) { // Ensure non-negative
          valuesToSave[id] = numValue;
          hasValidValue = true;
        } else {
          console.warn(`Invalid or negative input for nutrient ${id}: "${value}". Skipping.`);
          // Optionally provide feedback to the user here
        }
      } else {
         // If user cleared the input, send null to indicate deletion (if backend supports it)
         // If backend upserts, sending nothing is fine.
         // Let's assume upsert for now and only send valid numbers.
         // valuesToSave[id] = null; 
      }
    });

    if (!hasValidValue) {
      console.log("No valid nutrient values entered to save.");
      // TODO: Add user feedback (e.g., toast notification)
      return;
    }

    saveMutation.mutate({ 
      dishId: selectedItem.id, 
      nutrientValues: valuesToSave,
      schoolId: schoolId // Pass schoolId needed by the API function
    });

  }, [selectedItem, nutrientValues, isAtLeastOneValueFilled, saveMutation, schoolId]); // Added schoolId
  
  const handleSkipItem = useCallback(() => {
    if (!selectedItem || items.length <= 1) return;
    const currentIndex = items.findIndex(item => item.id === selectedItem.id);
    const nextIndex = (currentIndex + 1) % items.length;
    handleSelectItem(items[nextIndex]);
  }, [selectedItem, items, handleSelectItem]);
  
  const handleRetry = () => {
    refetchQueue();
  };
  
  // Delete functionality (adapted)
  const handleOpenDeleteModal = useCallback(() => {
    if (!selectedItem) return;
    setDeleteModalOpen(true);
  }, [selectedItem]);
  
  const handleDeleteSuccess = useCallback(() => {
    console.log(`[NutrientQueue] Deletion successful for item ID: ${selectedItem?.id}. Invalidating queue...`);
    // Invalidate both the nutrient queue and the missing translations queue
    queryClient.invalidateQueries({ queryKey: ['nutrientQueue', schoolId] });
    queryClient.invalidateQueries({ queryKey: ['missingTranslations', schoolId] });
     
    // Clear the selected item after invalidation starts
    setSelectedItem(null); 
  }, [selectedItem?.id, queryClient, schoolId]);
  
  // NEW: Callback function for when bulk import is complete
  const handleBulkImportComplete = useCallback((result) => {
    if (result.success) {
      console.log('[NutrientQueue] Bulk import completed successfully. Updating cache...');
      if (result.updatedMissingItems) {
        // Directly update cache with the fresh list from the backend
        queryClient.setQueryData(['nutrientQueue', schoolId], result.updatedMissingItems);
        console.log(`[NutrientQueue] Cache updated directly with ${result.updatedMissingItems.length} items.`);
      } else {
        // Fallback: Invalidate if backend didn't return the updated list
        console.log('[NutrientQueue] Backend did not return updated list, invalidating cache.');
        queryClient.invalidateQueries({ queryKey: ['nutrientQueue', schoolId] });
      }
      // Switch back to individual tab after successful import
      setActiveTab('individual'); 

      // --- ADD INVALIDATION FOR PUBLIC MENU HERE ---
      const subdomain = userDetails?.current_school?.subdomain;
      if (subdomain) {
        console.log(`[NutrientQueueBulkComplete] Invalidating public menu cache for subdomain: ${subdomain}`);
        // Invalidate the public menu query to refresh nutrient status
        queryClient.invalidateQueries({ queryKey: ['publicMenu', subdomain], refetchType: 'all' });
      } else {
        console.warn('[NutrientQueueBulkComplete] Could not invalidate public menu cache: subdomain not found in userDetails.');
      }
      // --- END PUBLIC MENU INVALIDATION ---
    } else {
      // Error is handled within the BulkNutrientSection modal
      console.log('[NutrientQueue] Bulk import failed.');
    }
  }, [queryClient, schoolId, userDetails, setActiveTab]);

  // --- Render Logic --- 

  if (initError) {
    return (
      <>
        <Navbar isAdmin={true} />
        <div className="max-w-7xl mx-auto p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">{t('general.error')}</p>
            <p>{initError}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar isAdmin={true} />
      <div className="max-w-7xl mx-auto p-4">
        <h1 className="text-3xl font-bold mb-6">{t('admin.nutrientQueue')}</h1>
        
        <div className="bg-white rounded-lg overflow-hidden mb-6">
          {/* Tabs (Individual / Bulk) */}  
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'individual'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('individual')}
              >
                {t('queue.individualInput')}
              </button>
              <button
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'bulk'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('bulk')}
              >
                {t('queue.bulkImportExport')}
              </button>
            </nav>
          </div>

          {/* Bulk Section Placeholder */} 
          {activeTab === 'bulk' && (
             <div className="p-6">
                <BulkNutrientSection 
                  schoolId={schoolId}
                  enabledNutrients={enabledNutrients}
                  onImportComplete={handleBulkImportComplete}
                />
             </div>
          )}
        </div>
          
        {loading && activeTab === 'individual' && (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="large" />
          </div>
        )}
        
        {!initError && isQueueError && activeTab === 'individual' && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">{t('general.error')}</p>
            <p>{queueFetchError?.message || t('queue.errorLoadingNutrientQueue')}</p>
            <button onClick={handleRetry} className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded">
              {t('general.retry')}
            </button>
          </div>
        )}
        
        {!initError && !loading && !isQueueError && items.length === 0 && activeTab === 'individual' && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">{t('queue.nutrientQueueCompleteTitle')}</p>
            <p>{t('queue.nutrientQueueCompleteMessage')}</p>
          </div>
        )}
        
        {/* Individual Input Interface */} 
        {!initError && !loading && !isQueueError && items.length > 0 && activeTab === 'individual' && (
          <div className="flex flex-col md:flex-row gap-6 bg-white rounded-lg shadow-md p-4">
            {/* Left sidebar - Items list */} 
            <div className="w-full md:w-1/3 border-r border-gray-200 pr-4">
              <h2 className="text-xl font-semibold mb-3">{t('queue.dishesMissingNutrients')} ({items.length})</h2>
              {/* <p className="text-sm text-gray-600 mb-4">Progress: {stats.completed}/{stats.total} ({Math.round((stats.completed / stats.total) * 100) || 0}%)</p> */}
              
              <div className="space-y-2 max-h-[32rem] overflow-y-auto">
                {items.map(item => (
                  <div 
                    key={`dish-${item.id}`}
                    className={`p-3 rounded cursor-pointer transition-colors ${
                      selectedItem?.id === item.id ? 
                      'bg-blue-100 border-l-4 border-blue-500' : 
                      'hover:bg-gray-100 border-l-4 border-transparent'
                    }`}
                    onClick={() => handleSelectItem(item)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{getDisplayName(item)}</span>
                        <p className="text-xs text-gray-500">{getDisplayType(item.type)}</p>
                      </div>
                      {/* Optionally show count of missing nutrients */} 
                       <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                         {item.missingNutrients?.length || 0} {t('queue.missing')}
                       </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right content - Nutrient input form */} 
            <div className="w-full md:w-2/3 pl-4">
              {selectedItem ? (
                <>
                  <h2 className="text-xl font-semibold mb-3">
                    {t('queue.editNutrientsFor')}: {getDisplayName(selectedItem)}
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('queue.missingNutrients')}: {selectedItem.missingNutrients.map(id => getNutrientInfo(id).name).join(', ') || t('none')}
                  </p>
                  
                  {saveMutation.isSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                      {t('queue.nutrientValuesSaved')}
                    </div>
                  )}
                  
                  {saveMutation.isError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      <p className="font-bold">{t('general.error')}</p>
                      <p>{saveMutation.error?.message || t('queue.errorSavingNutrients')}</p>
                    </div>
                  )}
                  
                  <div className="space-y-4 mb-6 max-h-80 overflow-y-auto">
                    {selectedItem.missingNutrients?.length > 0 ? (
                      selectedItem.missingNutrients.map(nutrientId => {
                        const nutrientInfo = getNutrientInfo(nutrientId);
                        return (
                          <div key={nutrientId} className="form-group">
                            <label className="block text-gray-700 text-sm font-bold mb-1" htmlFor={`nutrient-${nutrientId}`}>
                              {nutrientInfo.name}
                              {nutrientInfo.unit && <span className="text-xs ml-1 text-gray-500">({nutrientInfo.unit})</span>}
                            </label>
                            <input
                              id={`nutrient-${nutrientId}`}
                              type="number"
                              step="any"
                              min="0"
                              className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                                !nutrientValues[nutrientId] ? 'border-yellow-300 bg-yellow-50' : ''
                              }`}
                              value={nutrientValues[nutrientId] ?? ''} // Use ?? to handle 0
                              onChange={(e) => handleNutrientValueChange(nutrientId, e.target.value)}
                              onWheel={(e) => e.target.blur()} // Prevent scroll wheel change
                              placeholder={t('queue.enterValue')}
                            />
                          </div>
                        );
                      })
                    ) : (
                      <p>{t('queue.noMissingNutrientsForItem')}</p> // Should ideally not happen if item is in queue
                    )}
                  </div>
                  
                  <div className="flex justify-between mt-4">
                    <button
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                      onClick={handleSkipItem}
                      disabled={items.length <= 1}
                    >
                      {t('queue.skipItem')}
                    </button>
                    <div className="space-x-2">
                      <button
                        className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                        onClick={handleOpenDeleteModal}
                      >
                        {t('queue.deleteDish')}
                      </button>
                      <button
                        className={`${ 
                          isAtLeastOneValueFilled() && !saveMutation.isLoading
                            ? 'bg-blue-500 hover:bg-blue-700' 
                            : 'bg-blue-300 cursor-not-allowed'
                        } text-white font-bold py-2 px-4 rounded`}
                        onClick={handleSaveChanges}
                        disabled={!isAtLeastOneValueFilled() || saveMutation.isLoading}
                      >
                        {saveMutation.isLoading ? t('saving') : t('queue.saveAndNext')}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex justify-center items-center h-64 text-gray-500">
                  {items.length > 0 
                    ? t('queue.selectDishToEnterNutrients')
                    : t('queue.nutrientQueueCompleteMessage')} 
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Delete Dish Modal */} 
      {selectedItem && (
        <DeleteDishModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onSuccess={handleDeleteSuccess}
          itemId={selectedItem?.id}
          itemType={'dish'} // Always dish for this queue
          schoolId={getSchoolId()}
          language={i18n.language}
        />
      )}
    </>
  );
};

export default NutrientQueuePage; 